import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Pencil, X, Check, RotateCcw, Loader2, History, AlertTriangle } from "lucide-react";
import { API } from "../config";
import { useAuth } from "../context/AuthContext";
import { OperationCenterHistoryModal } from "../components/admin/OperationCenterHistoryModal";

// Map our internal country_code (mostly ISO 3166-1 alpha-2 already, plus
// a few custom values like UK/GBZ/IRL) to the canonical ISO-2 lowercase
// code expected by https://flagcdn.com (the SVG provider we use).
const FLAGCDN_CODE_OVERRIDES = {
  UK: "gb",   // United Kingdom — ISO code is GB
  GBZ: "gi",  // Gibraltar       — ISO code is GI
  IRL: "ie",  // Ireland         — ISO code is IE
};

const flagUrl = (countryCode) => {
  const iso = (FLAGCDN_CODE_OVERRIDES[countryCode] || countryCode || "").toLowerCase();
  return iso ? `https://flagcdn.com/${iso}.svg` : null;
};

// Minimal phone-format guard: allow digits, spaces, "+" and dashes,
// 3-40 chars total. Backend runs the same sanity check via Pydantic.
const PHONE_RE = /^[+\d][\d\s\-().]{2,39}$/;

// Format an ISO 8601 UTC timestamp (e.g. "2026-02-05T14:32:00+00:00")
// into the compact "DD/MM/YY" the "Custom" badge shows on the country
// row. Falls back to an empty string when the timestamp is missing or
// malformed so the badge still renders without a date rather than a
// broken "NaN/NaN" label.
const formatOverrideDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
};

const OperationCentersPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin" || user?.role === "admin";
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCode, setEditingCode] = useState(null);
  const [draftPhone, setDraftPhone] = useState("");
  const [savingCode, setSavingCode] = useState(null);
  const [historyModal, setHistoryModal] = useState(null); // {code, name} | null

  const fetchCenters = async () => {
    try {
      const response = await axios.get(`${API}/operation-centers`);
      setCenters(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento delle centrali operative");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCenters(); }, []);

  const startEdit = (center) => {
    setEditingCode(center.country_code);
    setDraftPhone(center.phone);
  };

  const cancelEdit = () => {
    setEditingCode(null);
    setDraftPhone("");
  };

  const savePhone = async (countryCode) => {
    const phone = draftPhone.trim();
    if (!PHONE_RE.test(phone)) {
      toast.error("Formato non valido. Usa cifre, spazi, + o - (3-40 caratteri).");
      return;
    }
    setSavingCode(countryCode);
    try {
      const res = await axios.patch(`${API}/operation-centers/${countryCode}`, { phone });
      setCenters((prev) => prev.map((c) => (c.country_code === countryCode ? res.data : c)));
      toast.success(`Numero SOC aggiornato per ${res.data.country}`);
      cancelEdit();
    } catch (error) {
      const detail = error.response?.data?.detail || "Errore aggiornamento numero";
      toast.error(typeof detail === "string" ? detail : "Errore aggiornamento numero");
    } finally {
      setSavingCode(null);
    }
  };

  const revertPhone = async (countryCode) => {
    if (!window.confirm("Ripristinare il numero SOC di default?")) return;
    setSavingCode(countryCode);
    try {
      const res = await axios.delete(`${API}/operation-centers/${countryCode}/override`);
      setCenters((prev) => prev.map((c) => (c.country_code === countryCode ? res.data : c)));
      toast.success(`Numero SOC ripristinato al default per ${res.data.country}`);
    } catch (error) {
      const detail = error.response?.data?.detail || "Errore ripristino numero";
      toast.error(typeof detail === "string" ? detail : "Errore ripristino numero");
    } finally {
      setSavingCode(null);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto animate-fade-in" data-testid="operation-centers-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white">Centrali Operative</h1>
        <p className="text-gray-400 mt-1">
          Numeri di emergenza per paese
          {isAdmin && <span className="text-gray-500 text-sm"> · click sull&apos;icona di modifica per aggiornare il numero SOC</span>}
        </p>
      </div>
      {loading ? (
        <div className="flex items-center justify-center min-h-[400px]"><div className="spinner"></div></div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {centers.map((center) => {
            const url = flagUrl(center.country_code);
            const isEditing = editingCode === center.country_code;
            const isSaving = savingCode === center.country_code;
            return (
              <div key={center.country_code} className="wallet-card" data-testid={`center-card-${center.country_code}`}>
                <div className="flex items-center gap-3 mb-4">
                  {url ? (
                    <img
                      src={url}
                      alt={`${center.country} flag`}
                      className="w-12 h-9 object-cover rounded-sm shadow-sm border border-white/10 flex-shrink-0"
                      loading="lazy"
                      onError={(e) => {
                        const span = document.createElement("span");
                        span.textContent = (center.country_code || "?").toUpperCase();
                        span.className = "inline-flex w-12 h-9 items-center justify-center text-xs font-bold text-white/70 bg-white/5 rounded-sm border border-white/10";
                        e.currentTarget.replaceWith(span);
                      }}
                    />
                  ) : (
                    <span className="inline-flex w-12 h-9 items-center justify-center text-xs font-bold text-white/70 bg-white/5 rounded-sm border border-white/10">
                      {(center.country_code || "?").toUpperCase()}
                    </span>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl font-semibold text-white truncate">{center.country}</h3>
                    <p className="text-gray-500 text-sm truncate">{center.label}</p>
                    {center.override_active && (
                      <span
                        className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30 text-[11px] font-medium"
                        title={
                          center.override_updated_at
                            ? `Numero SOC personalizzato — ultima modifica ${formatOverrideDate(center.override_updated_at)}`
                            : "Numero SOC personalizzato"
                        }
                        data-testid={`override-badge-${center.country_code}`}
                      >
                        <AlertTriangle size={11} className="flex-shrink-0" />
                        Custom{center.override_updated_at ? ` dal ${formatOverrideDate(center.override_updated_at)}` : ""}
                      </span>
                    )}
                  </div>
                  {isAdmin && !isEditing && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => setHistoryModal({ code: center.country_code, name: center.country })}
                        className="p-2 rounded-md text-gray-400 hover:text-white hover:bg-white/5 transition"
                        title="Cronologia modifiche"
                        data-testid={`history-phone-${center.country_code}`}
                      >
                        <History size={14} />
                      </button>
                      <button
                        onClick={() => startEdit(center)}
                        className="p-2 rounded-md text-gray-400 hover:text-white hover:bg-white/5 transition"
                        title="Modifica numero SOC"
                        data-testid={`edit-phone-${center.country_code}`}
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={() => revertPhone(center.country_code)}
                        className="p-2 rounded-md text-gray-500 hover:text-gray-200 hover:bg-white/5 transition"
                        title="Ripristina default"
                        data-testid={`revert-phone-${center.country_code}`}
                      >
                        <RotateCcw size={14} />
                      </button>
                    </div>
                  )}
                </div>
                {isEditing ? (
                  <div className="flex items-center gap-2" data-testid={`edit-row-${center.country_code}`}>
                    <input
                      type="tel"
                      value={draftPhone}
                      onChange={(e) => setDraftPhone(e.target.value)}
                      className="flex-1 bg-[#111] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-lojack-red"
                      placeholder="+39 02 12345678"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter") savePhone(center.country_code);
                        if (e.key === "Escape") cancelEdit();
                      }}
                      data-testid={`phone-input-${center.country_code}`}
                    />
                    <button
                      onClick={() => savePhone(center.country_code)}
                      disabled={isSaving}
                      className="p-2 rounded-md bg-lojack-red hover:bg-lojack-red/80 text-white transition disabled:opacity-60"
                      title="Salva"
                      data-testid={`save-phone-${center.country_code}`}
                    >
                      {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                    </button>
                    <button
                      onClick={cancelEdit}
                      disabled={isSaving}
                      className="p-2 rounded-md bg-gray-800 hover:bg-gray-700 text-gray-200 transition"
                      title="Annulla"
                      data-testid={`cancel-phone-${center.country_code}`}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <a
                    href={`tel:${center.phone}`}
                    className="phone-link text-xl"
                    data-testid={`call-${center.country_code}`}
                  >
                    <Phone size={20} />{center.phone}
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}

      {historyModal && (
        <OperationCenterHistoryModal
          countryCode={historyModal.code}
          countryName={historyModal.name}
          onClose={() => setHistoryModal(null)}
        />
      )}
    </div>
  );
};

export default OperationCentersPage;
